import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Loader2,
  Download,
  Search,
  RefreshCw,
  Users,
  UserCheck,
  GraduationCap,
  UserPlus,
  ShieldCheck,
  Calendar,
  Filter,
  X,
} from "lucide-react";

/**
 * SignupsTab — unified sign-up export.
 *
 * Combines two data sources into one export flow:
 *   • `users` collection      → registered donors / students / admins
 *   • `beta_signups` collection → pre-launch waitlist leads
 *
 * Admin picks filters (user type, source kind, date range, email search),
 * sees a live preview, then clicks Download CSV to pull a targeted list.
 *
 * All backend logic lives in /app/backend/routes/admin_signups.py.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mra_jwt";
const PAGE_SIZE = 25;

const client = axios.create({ baseURL: API, withCredentials: true });
client.interceptors.request.use((cfg) => {
  const t = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  if (t) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${t}`;
  }
  return cfg;
});

const TYPE_OPTIONS = [
  { key: "donor",   label: "Donor",   icon: UserCheck,    tone: "text-cobalt bg-cobalt/[0.08]" },
  { key: "student", label: "Student", icon: GraduationCap,tone: "text-grass bg-grass/[0.08]" },
  { key: "lead",    label: "Lead",    icon: UserPlus,     tone: "text-olive bg-olive/[0.08]" },
  { key: "admin",   label: "Admin",   icon: ShieldCheck,  tone: "text-tangerine bg-tangerine/[0.08]" },
];

const KIND_OPTIONS = [
  { key: "",           label: "All sources" },
  { key: "registered", label: "Registered accounts" },
  { key: "waitlist",   label: "Waitlist leads" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

const typeMeta = (t) =>
  TYPE_OPTIONS.find((o) => o.key === t) || { label: t, tone: "bg-bone text-ink" };

export const SignupsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [err, setErr] = useState("");
  const [page, setPage] = useState(1);

  // Filters
  const [types, setTypes] = useState(() => new Set()); // empty = all
  const [kind, setKind] = useState("");                // "" = all
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [q, setQ] = useState("");

  const params = useMemo(() => {
    const p = new URLSearchParams();
    p.set("page", String(page));
    p.set("page_size", String(PAGE_SIZE));
    if (types.size > 0) p.set("user_type", Array.from(types).join(","));
    if (kind) p.set("source_kind", kind);
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    if (q.trim()) p.set("q", q.trim());
    return p;
  }, [page, types, kind, dateFrom, dateTo, q]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await client.get(`/admin/signups?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't load sign-ups. Try Refresh in a moment.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  const filtersActive = types.size > 0 || !!kind || !!dateFrom || !!dateTo || !!q.trim();

  const toggleType = (t) => {
    setTypes((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t); else next.add(t);
      return next;
    });
    setPage(1);
  };

  const clearFilters = () => {
    setTypes(new Set());
    setKind("");
    setDateFrom("");
    setDateTo("");
    setQ("");
    setPage(1);
  };

  const downloadCsv = async () => {
    setDownloading(true);
    setErr("");
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      // CSV endpoint accepts the same filter params — no page/page_size (full stream).
      const exportParams = new URLSearchParams(params);
      exportParams.delete("page");
      exportParams.delete("page_size");
      const url = `${API}/admin/signups/export.csv?${exportParams.toString()}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`Export failed (HTTP ${r.status})`);
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `myrichaunt-signups-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <section data-testid="signups-tab" className="space-y-6">
      {/* Rollup strip */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <RollupCard label="Matching total" value={data?.total ?? "—"} icon={<Users className="h-3.5 w-3.5" strokeWidth={2} />} testid="signups-total" featured />
        {TYPE_OPTIONS.map((t) => {
          const Icon = t.icon;
          return (
            <RollupCard
              key={t.key}
              label={`${t.label}s`}
              value={data?.type_counts?.[t.key] ?? 0}
              icon={<Icon className="h-3.5 w-3.5" strokeWidth={2} />}
              testid={`signups-count-${t.key}`}
            />
          );
        })}
        <RollupCard
          label="Sources"
          testid="signups-count-sources"
          value={
            data ? (
              <span className="flex flex-col gap-0.5 font-serif text-lg leading-tight text-ink">
                <span><span>{data.kind_counts.registered || 0}</span> <span className="text-[10px] font-medium uppercase tracking-wide text-olive">reg.</span></span>
                <span><span>{data.kind_counts.waitlist || 0}</span> <span className="text-[10px] font-medium uppercase tracking-wide text-olive">wait.</span></span>
              </span>
            ) : "—"
          }
        />
      </div>

      {/* Filter panel */}
      <div className="rounded-xl border border-border bg-bone p-5">
        <div className="flex items-start justify-between gap-3">
          <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-olive">
            <Filter className="h-3.5 w-3.5" strokeWidth={2} /> Filter before exporting
          </p>
          {filtersActive && (
            <button
              type="button"
              onClick={clearFilters}
              data-testid="signups-clear-filters"
              className="inline-flex items-center gap-1 rounded-full bg-sand px-2.5 py-1 text-[11px] font-semibold text-ink hover:bg-beige"
            >
              <X className="h-3 w-3" strokeWidth={2} /> Clear all
            </button>
          )}
        </div>

        {/* User type toggles */}
        <div className="mt-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-olive">User type</p>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="signups-type-toggles">
            {TYPE_OPTIONS.map((t) => {
              const active = types.has(t.key);
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => toggleType(t.key)}
                  data-testid={`signups-type-${t.key}`}
                  aria-pressed={active}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    active
                      ? "border-ink bg-ink text-sand"
                      : "border-border bg-sand text-ink hover:border-ink"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" strokeWidth={2} />
                  {t.label}
                </button>
              );
            })}
            <span className="ml-1 self-center text-[11px] italic text-olive">
              {types.size === 0 ? "All types included" : `${types.size} type${types.size === 1 ? "" : "s"} selected`}
            </span>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Source kind */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-olive">Source</p>
            <div className="mt-2 inline-flex items-center gap-1 rounded-full border border-border bg-sand p-1 text-xs font-semibold" data-testid="signups-kind-toggle">
              {KIND_OPTIONS.map((o) => (
                <button
                  key={o.key || "all"}
                  type="button"
                  onClick={() => { setKind(o.key); setPage(1); }}
                  data-testid={`signups-kind-${o.key || "all"}`}
                  className={`rounded-full px-3 py-1 transition-colors ${kind === o.key ? "bg-ink text-sand" : "text-ink hover:bg-beige"}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {/* Date range */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-olive">Date range</p>
            <div className="mt-2 flex items-center gap-2 rounded-full border border-border bg-sand px-3 py-1">
              <Calendar className="h-3.5 w-3.5 text-olive" strokeWidth={2} />
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                data-testid="signups-date-from"
                aria-label="Registered from"
                className="w-full bg-transparent text-xs text-ink outline-none"
              />
              <span className="text-xs text-olive/60">→</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                data-testid="signups-date-to"
                aria-label="Registered to"
                className="w-full bg-transparent text-xs text-ink outline-none"
              />
            </div>
          </div>

          {/* Email search */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-olive">Search email</p>
            <label className="relative mt-2 block">
              <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-olive" strokeWidth={2} />
              <input
                type="search"
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                placeholder="e.g. @stanford.edu"
                data-testid="signups-search"
                className="w-full rounded-full border border-border bg-sand py-1.5 pl-9 pr-3 text-xs text-ink placeholder:text-olive/60 focus:border-ink focus:outline-none"
              />
            </label>
          </div>
        </div>
      </div>

      {/* Toolbar — refresh + download */}
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-xs text-olive">
          {loading ? (
            "Loading preview…"
          ) : data ? (
            <>
              Preview of{" "}
              <span className="font-semibold text-ink">
                {Math.min(data.rows.length, data.page_size)}
              </span>
              {" "}of{" "}
              <span className="font-semibold text-ink">{data.total.toLocaleString()}</span> matching sign-ups
              {filtersActive ? " (filtered)" : ""}
            </>
          ) : ""}
        </p>

        <button
          type="button"
          onClick={load}
          data-testid="signups-refresh"
          className="ml-auto inline-flex items-center gap-1 rounded-full border border-border bg-sand px-3 py-1.5 text-xs font-semibold text-ink hover:border-ink"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} strokeWidth={2} /> Refresh
        </button>

        <button
          type="button"
          onClick={downloadCsv}
          disabled={downloading || loading || !data || data.total === 0}
          data-testid="signups-download-csv"
          className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-1.5 text-xs font-semibold text-sand transition-colors hover:bg-forest disabled:opacity-50"
        >
          {downloading ? (
            <><Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} /> Preparing…</>
          ) : (
            <><Download className="h-3.5 w-3.5" strokeWidth={2} /> Download CSV{data && data.total > 0 ? ` (${data.total.toLocaleString()})` : ""}</>
          )}
        </button>
      </div>

      {err && (
        <p role="alert" data-testid="signups-error" className="rounded-md bg-scarlet/8 px-3 py-2 text-sm text-scarlet">
          {err}
        </p>
      )}

      {/* Preview table */}
      <div className="overflow-hidden rounded-xl border border-border bg-sand">
        <table className="min-w-full text-sm">
          <thead className="bg-bone text-[10px] font-semibold uppercase tracking-[0.18em] text-olive">
            <tr>
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left">Email</th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left">Source</th>
              <th className="px-4 py-3 text-left">Registered</th>
              <th className="px-4 py-3 text-left">Campaigns</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading && !data && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-olive">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" strokeWidth={2} />
                </td>
              </tr>
            )}
            {data && data.rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center">
                  <Users className="mx-auto h-6 w-6 text-olive/50" strokeWidth={1.5} />
                  <p className="mt-3 text-sm text-olive" data-testid="signups-empty">
                    {filtersActive ? "No sign-ups match your filters." : "No sign-ups yet."}
                  </p>
                </td>
              </tr>
            )}
            {data && data.rows.map((r, i) => {
              const meta = typeMeta(r.user_type);
              return (
                <motion.tr
                  key={`${r.email}-${r.registered_at}-${r.source_kind}`}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: Math.min(i * 0.01, 0.2) }}
                  className="hover:bg-bone/60"
                  data-testid={`signups-row-${i}`}
                >
                  <td className="px-4 py-3 font-medium text-ink">
                    {r.name || <span className="italic text-olive/60">—</span>}
                  </td>
                  <td className="px-4 py-3 text-ink" data-testid={`signups-row-${i}-email`}>{r.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${meta.tone}`}>
                      {meta.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[11px] font-mono text-olive">
                    {r.source_kind === "registered"
                      ? <span className="italic">registered</span>
                      : (r.source || <span className="italic">waitlist</span>)}
                  </td>
                  <td className="px-4 py-3 text-olive">{fmtDate(r.registered_at)}</td>
                  <td className="px-4 py-3 text-[11px] text-olive">
                    {r.campaigns.length === 0
                      ? <span className="italic text-olive/60">—</span>
                      : r.campaigns.slice(0, 2).join(", ") + (r.campaigns.length > 2 ? ` +${r.campaigns.length - 2}` : "")}
                  </td>
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data && data.total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-olive" data-testid="signups-pagination">
          <span>
            Showing <span className="font-semibold text-ink">{(page - 1) * PAGE_SIZE + 1}</span>–
            <span className="font-semibold text-ink">{Math.min(page * PAGE_SIZE, data.total)}</span> of{" "}
            <span className="font-semibold text-ink">{data.total.toLocaleString()}</span>
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              data-testid="signups-page-prev"
              className="rounded-full border border-border bg-sand px-3 py-1 font-semibold hover:border-ink disabled:opacity-50"
            >
              Previous
            </button>
            <span className="font-semibold text-ink">Page {page} / {totalPages}</span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              data-testid="signups-page-next"
              className="rounded-full border border-border bg-sand px-3 py-1 font-semibold hover:border-ink disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

const RollupCard = ({ label, value, icon, testid, featured = false }) => (
  <div
    className={`rounded-xl border p-4 ${featured ? "border-ink bg-ink text-sand" : "border-border bg-bone"}`}
    data-testid={testid}
  >
    <p className={`flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.22em] ${featured ? "text-sand/80" : "text-olive"}`}>
      {icon}{label}
    </p>
    <div className={`mt-1 font-serif text-2xl leading-tight ${featured ? "text-sand" : "text-ink"}`}>
      {value}
    </div>
  </div>
);
